import {defineStore} from 'pinia'
import {startAccountOAuth} from '@/scripts/accountOAuth'
import {matchaParams} from '@/scripts/matcha'
import Swal from 'sweetalert2'
import Cookies from 'js-cookie'
import User from '../models/User'
import * as constants from './constants'

const AUTH_STATUS = {
    BOOTSTRAPPING: 'bootstrapping',
    AUTHENTICATED: 'authenticated',
    REFRESHING: 'refreshing',
    ANONYMOUS: 'anonymous',
    ERROR: 'error',
}

const REFRESH_MARGIN_MS = 5 * 60 * 1000
const MIN_REFRESH_DELAY_MS = 15 * 1000
const REMOTE_REFRESH_WAIT_MS = 8000
const REMOTE_REFRESH_POLL_INTERVAL_MS = 400
const REFRESH_LOCK_TTL_MS = 30 * 1000
const SESSION_BROADCAST_CHANNEL = 'manet_ccp_auth_channel'
const SESSION_SYNC_KEY = 'manet_ccp_auth_sync'
const REFRESH_LOCK_KEY = 'manet_ccp_auth_refresh_lock'
const IMPERSONATION_STORAGE_KEY = 'manet_ccp_impersonation'
const POST_LOGIN_REDIRECT_STORAGE_KEY = 'manet_ccp_post_login_redirect'
const AUTH_EXCLUDED_PATHS = ['/auth/login', '/auth/refresh', '/auth/logout', '/auth/callback', '/account_confirmation']

const canUseWindow = () => typeof window !== 'undefined'

const createTabId = () => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID()
    }
    return `tab_${Date.now()}_${Math.random().toString(16).slice(2)}`
}

const safeParse = (value) => {
    if (!value) {
        return null
    }

    try {
        return JSON.parse(value)
    } catch (_) {
        return null
    }
}

const wait = (ms) => new Promise((resolve) => {
    setTimeout(resolve, ms)
})

export const useUserStore = defineStore('user', {
    state: () => ({
        user: User.loggedOutUser(),
        created: false,
        allClients: [],
        myLocations: [],
        myLocationsTotal: 0,
        myLocationsFetched: false,
        myLocationsLoading: false,
        loadingCount: 0,
        authReady: false,
        authStatus: AUTH_STATUS.BOOTSTRAPPING,
        accessToken: null,
        expiresAt: null,
        refreshPromise: null,
        initializePromise: null,
        refreshTimeoutId: null,
        lastAuthEventAt: null,
        lifecycleBound: false,
        broadcastChannel: null,
        tabId: createTabId(),
        loggingOut: false,
        impersonation: null,
        stoppingImpersonation: false,
        routeImpersonationPromise: null,
        routeImpersonationKdnr: null,
    }),
    getters: {
        loading: (state) => state.loadingCount > 0,
        isAuthenticated: (state) => state.authStatus === AUTH_STATUS.AUTHENTICATED && !!state.accessToken && state.user.isLoggedIn,
        hasActiveSession: (state) => [AUTH_STATUS.AUTHENTICATED, AUTH_STATUS.REFRESHING].includes(state.authStatus) && !!state.accessToken && state.user.isLoggedIn,
        isImpersonating: (state) => !!state.impersonation?.id,
    },
    actions: {
        getPersistedToken() {
            return Cookies.get(constants.AUTH_COOKIE_NAME) || null
        },
        persistToken(token) {
            if (!token) {
                Cookies.remove(constants.AUTH_COOKIE_NAME)
                return
            }

            Cookies.set(constants.AUTH_COOKIE_NAME, token, {expires: constants.AUTH_COOKIE_DURATION})
        },
        clearPersistedToken() {
            Cookies.remove(constants.AUTH_COOKIE_NAME)
        },
        getPersistedImpersonation() {
            if (!canUseWindow()) {
                return null
            }

            return safeParse(localStorage.getItem(IMPERSONATION_STORAGE_KEY))
        },
        clearPersistedImpersonation() {
            if (canUseWindow()) {
                localStorage.removeItem(IMPERSONATION_STORAGE_KEY)
            }
        },
        consumeLegacyImpersonationMarker() {
            const legacyImpersonation = this.getPersistedImpersonation()
            this.clearPersistedImpersonation()
            return !!legacyImpersonation?.originalToken
        },
        validateImpersonationContext(impersonation, user) {
            const tokenSessionId = user?.impersonationSessionId
            const contextSessionId = impersonation?.id

            if (!tokenSessionId && !contextSessionId && !impersonation) {
                return null
            }

            if (
                !tokenSessionId
                || !contextSessionId
                || String(tokenSessionId) !== String(contextSessionId)
            ) {
                const error = new Error('Impersonation context does not match the auth token')
                error.code = 'IMPERSONATION_CONTEXT_MISMATCH'
                throw error
            }

            return impersonation
        },
        setImpersonationContext(impersonation, {broadcast = true} = {}) {
            this.impersonation = impersonation || null
            this.clearPersistedImpersonation()

            if (broadcast) {
                this.broadcastAuthEvent('impersonation-updated', {impersonation: this.impersonation})
            }
        },
        clearImpersonationContext({broadcast = true} = {}) {
            this.impersonation = null
            this.clearPersistedImpersonation()

            if (broadcast) {
                this.broadcastAuthEvent('impersonation-cleared')
            }
        },
        isAuthExcludedRequest(url = '') {
            return AUTH_EXCLUDED_PATHS.some((path) => url.includes(path))
        },
        getTokenExpiry(token) {
            const decodedUser = User.from(token)
            return decodedUser.exp ? decodedUser.exp * 1000 : null
        },
        isTokenStale({bufferMs = REFRESH_MARGIN_MS, token = this.accessToken, expiresAt = this.expiresAt} = {}) {
            if (!token || !expiresAt) {
                return true
            }

            return Date.now() >= expiresAt - bufferMs
        },
        clearRefreshTimeout() {
            if (!this.refreshTimeoutId) {
                return
            }

            clearTimeout(this.refreshTimeoutId)
            this.refreshTimeoutId = null
        },
        scheduleRefresh() {
            this.clearRefreshTimeout()

            if (!this.isAuthenticated || !this.expiresAt) {
                return
            }

            const delay = Math.max(this.expiresAt - REFRESH_MARGIN_MS - Date.now(), MIN_REFRESH_DELAY_MS)
            this.refreshTimeoutId = setTimeout(() => {
                this.ensureFreshSession('timer').catch((error) => {
                    console.error('scheduled ensureFreshSession failed', error)
                })
            }, delay)
        },
        preserveUserState(nextUser) {
            if (!this.user?.id || this.user.id !== nextUser.id) {
                return
            }

            nextUser.first_name = this.user.first_name
            nextUser.last_name = this.user.last_name
            nextUser.email = this.user.email
            nextUser.active = this.user.active
            nextUser.password_change_supported = this.user.password_change_supported
            nextUser.groups = this.user.groups
            nextUser.stats = this.user.stats
            nextUser.surveyCompleted = this.user.surveyCompleted
            nextUser.address = this.user.address
            nextUser.invoice_address = this.user.invoice_address
            nextUser.organisations = this.user.organisations
            nextUser.organisation = {...this.user.organisation}
            nextUser.isDataFetched = this.user.isDataFetched
        },
        setSession(token, {
            preserveProfile = false,
            broadcast = true,
            markReady = true,
            impersonation,
        } = {}) {
            const nextUser = User.from(token)
            if (!nextUser.isLoggedIn || !nextUser.exp) {
                throw new Error('invalid auth token')
            }

            if (preserveProfile) {
                this.preserveUserState(nextUser)
            }

            let nextImpersonation = this.impersonation
            if (typeof impersonation !== 'undefined') {
                nextImpersonation = this.validateImpersonationContext(impersonation, nextUser)
            } else if (!nextUser.impersonationSessionId) {
                nextImpersonation = null
            }

            this.persistToken(token)
            this.user = nextUser
            this.accessToken = token
            this.expiresAt = nextUser.exp * 1000
            this.authStatus = AUTH_STATUS.AUTHENTICATED
            this.lastAuthEventAt = Date.now()

            if (markReady) {
                this.authReady = true
            }

            this.setImpersonationContext(nextImpersonation, {broadcast: false})

            this.scheduleRefresh()

            if (broadcast) {
                this.broadcastAuthEvent('session-updated', {
                    token,
                    impersonation: this.impersonation,
                })
            }

            return nextUser
        },
        resetAuthState() {
            this.clearRefreshTimeout()
            this.accessToken = null
            this.expiresAt = null
            this.refreshPromise = null
            this.user = User.loggedOutUser()
            this.lastAuthEventAt = Date.now()
            this.clearImpersonationContext({broadcast: false})
        },
        async initializeAuth() {
            if (this.authReady && this.authStatus !== AUTH_STATUS.BOOTSTRAPPING) {
                return this.isAuthenticated
            }

            if (this.initializePromise) {
                return this.initializePromise
            }

            this.bindSessionLifecycle()
            this.authStatus = AUTH_STATUS.BOOTSTRAPPING

            this.initializePromise = (async () => {
                const token = this.getPersistedToken()
                const hadLegacyImpersonation = this.consumeLegacyImpersonationMarker()
                if (!token) {
                    this.resetAuthState()
                    this.authStatus = AUTH_STATUS.ANONYMOUS
                    this.authReady = true
                    return false
                }

                try {
                    this.setSession(token, {preserveProfile: true, broadcast: false, markReady: false})
                    await this.ensureFreshSession('bootstrap')
                    if (this.isAuthenticated) {
                        await this.apiUserData()
                    }
                    const requiresConfirmedImpersonation = hadLegacyImpersonation
                        || !!this.user.impersonationSessionId
                    if (requiresConfirmedImpersonation && !this.isImpersonating) {
                        await this.clearSession({
                            redirectToLogin: true,
                            remoteLogout: false,
                            reason: 'impersonation_expired',
                        })
                        return false
                    }
                    this.authStatus = this.isAuthenticated ? AUTH_STATUS.AUTHENTICATED : AUTH_STATUS.ANONYMOUS
                    this.authReady = true
                    return this.isAuthenticated
                } catch (error) {
                    console.error('initializeAuth failed', error)
                    if (hadLegacyImpersonation || this.user.impersonationSessionId) {
                        await this.clearSession({
                            redirectToLogin: true,
                            remoteLogout: false,
                            reason: 'impersonation_expired',
                        })
                        return false
                    }
                    if (error?.response?.status >= 500 && this.accessToken && this.user.isLoggedIn) {
                        this.authStatus = AUTH_STATUS.AUTHENTICATED
                        this.authReady = true
                        this.scheduleRefresh()
                        return true
                    }
                    await this.clearSession({redirectToLogin: false, remoteLogout: false, broadcast: false, status: AUTH_STATUS.ANONYMOUS})
                    return false
                } finally {
                    this.initializePromise = null
                }
            })()

            return this.initializePromise
        },
        bindSessionLifecycle() {
            if (this.lifecycleBound || !canUseWindow()) {
                return
            }

            this.lifecycleBound = true

            if (typeof BroadcastChannel !== 'undefined') {
                this.broadcastChannel = new BroadcastChannel(SESSION_BROADCAST_CHANNEL)
                this.broadcastChannel.onmessage = (event) => {
                    this.handleAuthEvent(event.data)
                }
            }

            window.addEventListener('storage', this.onStorageEvent)
            window.addEventListener('focus', this.onWindowFocus)
            window.addEventListener('online', this.onWindowOnline)
            document.addEventListener('visibilitychange', this.onVisibilityChange)
        },
        onStorageEvent(event) {
            if (event.key !== SESSION_SYNC_KEY || !event.newValue) {
                return
            }

            const payload = safeParse(event.newValue)
            if (payload) {
                this.handleAuthEvent(payload)
            }
        },
        onWindowFocus() {
            this.ensureFreshSession('focus').catch((error) => {
                console.error('focus auth recovery failed', error)
            })
        },
        onWindowOnline() {
            this.ensureFreshSession('online').catch((error) => {
                console.error('online auth recovery failed', error)
            })
        },
        onVisibilityChange() {
            if (document.visibilityState !== 'visible') {
                return
            }

            this.ensureFreshSession('visibility').catch((error) => {
                console.error('visibility auth recovery failed', error)
            })
        },
        broadcastAuthEvent(type, payload = {}) {
            if (!canUseWindow()) {
                return
            }

            // Pinia wraps state values in Vue proxies. Serialize once so both
            // cross-tab transports receive the same structured-clone-safe data.
            const serializedEvent = JSON.stringify({
                type,
                payload,
                source: this.tabId,
                at: Date.now(),
            })
            const event = JSON.parse(serializedEvent)

            if (this.broadcastChannel) {
                this.broadcastChannel.postMessage(event)
            }

            localStorage.setItem(SESSION_SYNC_KEY, serializedEvent)
        },
        async handleAuthEvent(event) {
            if (!event || event.source === this.tabId) {
                return
            }

            switch (event.type) {
                case 'session-updated':
                    if (event.payload?.token) {
                        try {
                            const hasImpersonation = Object.prototype.hasOwnProperty.call(
                                event.payload,
                                'impersonation'
                            )
                            this.setSession(event.payload.token, {
                                preserveProfile: true,
                                broadcast: false,
                                impersonation: hasImpersonation ? event.payload.impersonation : undefined,
                            })
                            if (!this.user.isDataFetched) {
                                this.apiUserData().catch((error) => {
                                    console.error('remote session sync apiUserData failed', error)
                                })
                            }
                        } catch (error) {
                            console.error('failed to apply remote session update', error)
                            if (error?.code === 'IMPERSONATION_CONTEXT_MISMATCH') {
                                await this.clearSession({
                                    redirectToLogin: true,
                                    remoteLogout: false,
                                    reason: 'impersonation_expired',
                                })
                            }
                        }
                    }
                    break
                case 'session-cleared':
                    await this.clearSession({redirectToLogin: true, remoteLogout: false, broadcast: false, status: AUTH_STATUS.ANONYMOUS})
                    break
                case 'impersonation-updated':
                    if (event.payload?.impersonation) {
                        try {
                            this.setImpersonationContext(
                                this.validateImpersonationContext(event.payload.impersonation, this.user),
                                {broadcast: false}
                            )
                        } catch (error) {
                            await this.clearSession({
                                redirectToLogin: true,
                                remoteLogout: false,
                                reason: 'impersonation_expired',
                            })
                        }
                    }
                    break
                case 'impersonation-cleared':
                    if (this.user.impersonationSessionId) {
                        await this.clearSession({
                            redirectToLogin: true,
                            remoteLogout: false,
                            reason: 'impersonation_expired',
                        })
                    } else {
                        this.clearImpersonationContext({broadcast: false})
                    }
                    break
                case 'refresh-started':
                case 'refresh-failed':
                default:
                    this.lastAuthEventAt = event.at || Date.now()
                    break
            }
        },
        tryAcquireRefreshLock() {
            if (!canUseWindow()) {
                return true
            }

            const currentLock = safeParse(localStorage.getItem(REFRESH_LOCK_KEY))
            const now = Date.now()

            if (currentLock && currentLock.expiresAt > now && currentLock.owner !== this.tabId) {
                return false
            }

            const nextLock = {
                owner: this.tabId,
                expiresAt: now + REFRESH_LOCK_TTL_MS,
            }

            localStorage.setItem(REFRESH_LOCK_KEY, JSON.stringify(nextLock))
            const confirmedLock = safeParse(localStorage.getItem(REFRESH_LOCK_KEY))
            return confirmedLock?.owner === this.tabId
        },
        releaseRefreshLock() {
            if (!canUseWindow()) {
                return
            }

            const currentLock = safeParse(localStorage.getItem(REFRESH_LOCK_KEY))
            if (currentLock?.owner === this.tabId) {
                localStorage.removeItem(REFRESH_LOCK_KEY)
            }
        },
        async waitForRemoteRefresh() {
            const startedAt = Date.now()
            while (Date.now() - startedAt < REMOTE_REFRESH_WAIT_MS) {
                if (!this.isTokenStale({bufferMs: 1000})) {
                    return true
                }
                await wait(REMOTE_REFRESH_POLL_INTERVAL_MS)
            }
            return !this.isTokenStale({bufferMs: 1000})
        },
        async ensureFreshSession(reason = 'manual') {
            if (!this.accessToken) {
                const persistedToken = this.getPersistedToken()
                if (!persistedToken) {
                    return false
                }

                this.setSession(persistedToken, {preserveProfile: true, broadcast: false, markReady: this.authReady})
            }

            if (!this.isTokenStale()) {
                this.scheduleRefresh()
                return true
            }

            await this.refreshToken({reason})
            return this.isAuthenticated
        },
        async refreshToken({force = false, reason = 'manual'} = {}) {
            if (this.refreshPromise) {
                return this.refreshPromise
            }

            if (!this.accessToken) {
                throw new Error('cannot refresh without access token')
            }

            if (!force && !this.isTokenStale()) {
                this.scheduleRefresh()
                return this.accessToken
            }

            if (!this.tryAcquireRefreshLock()) {
                const refreshedByAnotherTab = await this.waitForRemoteRefresh()
                if (refreshedByAnotherTab) {
                    return this.accessToken
                }
            }

            this.authStatus = AUTH_STATUS.REFRESHING
            this.broadcastAuthEvent('refresh-started', {reason})

            this.refreshPromise = this.apiClient
                .post('/auth/refresh', {}, {_skipAuthRefresh: true, _skipUnauthorizedHandling: true})
                .then(({data}) => {
                    if (!data?.token) {
                        throw new Error('refresh response is missing token')
                    }

                    this.setSession(data.token, {
                        preserveProfile: true,
                        impersonation: data.impersonation || null,
                    })
                    return data.token
                })
                .catch(async (error) => {
                    this.broadcastAuthEvent('refresh-failed', {reason})
                    if (error?.code === 'IMPERSONATION_CONTEXT_MISMATCH') {
                        await this.clearSession({
                            redirectToLogin: true,
                            remoteLogout: false,
                            status: AUTH_STATUS.ANONYMOUS,
                            reason: 'impersonation_expired',
                        })
                    } else if (error?.response?.status === 401) {
                        await this.clearSession({
                            redirectToLogin: true,
                            remoteLogout: false,
                            status: AUTH_STATUS.ANONYMOUS,
                            reason: 'session_expired',
                        })
                    } else {
                        this.authStatus = AUTH_STATUS.AUTHENTICATED
                        this.scheduleRefresh()
                    }
                    throw error
                })
                .finally(() => {
                    this.refreshPromise = null
                    if (this.isAuthenticated) {
                        this.authStatus = AUTH_STATUS.AUTHENTICATED
                    }
                    this.releaseRefreshLock()
                })

            return this.refreshPromise
        },
        async clearSession({
            redirectToLogin = true,
            remoteLogout = false,
            broadcast = true,
            status = AUTH_STATUS.ANONYMOUS,
            reason = null,
        } = {}) {
            this.clearRefreshTimeout()

            const shouldRedirectToLogin = redirectToLogin && this.$router.currentRoute.value.name !== 'login'
            const previousAccessToken = this.accessToken

            if (shouldRedirectToLogin) {
                this.loggingOut = true
            }

            try {
                this.clearPersistedToken()
                this.resetAuthState()
                this.authStatus = status
                this.authReady = true

                if (broadcast) {
                    this.broadcastAuthEvent('session-cleared')
                }

                if (shouldRedirectToLogin) {
                    const loginRoute = {name: 'login'}
                    if (reason) {
                        loginRoute.query = {notice: reason}
                    }
                    try {
                        await this.$router.push(loginRoute)
                    } catch (_) {
                        // ignore duplicate or aborted navigation
                    }
                }

                if (remoteLogout && previousAccessToken) {
                    try {
                        await this.apiClient.post('/auth/logout', {}, {
                            _skipAuthRefresh: true,
                            _skipUnauthorizedHandling: true,
                            headers: {Authorization: `Bearer ${previousAccessToken}`},
                        })
                    } catch (error) {
                        // A 401 means the server-side session has already ended, which
                        // is the desired outcome for this idempotent operation.
                        if (error?.response?.status !== 401) {
                            console.error('logout failed', error)
                        }
                    }
                }
            } finally {
                this.loggingOut = false
            }
        },
        async finalizeLogin(token) {
            this.setSession(token, {preserveProfile: false, impersonation: null})
            await this.apiUserData()
            const redirect = this.consumePostLoginRedirect()
            await this.$router.push(redirect || {name: 'dashboard'})
            return token
        },
        login(provider, login = null, password = null, matchaToken = '') {
            return this.apiClient.post(
                '/auth/login',
                {login, password, ...matchaParams(matchaToken)},
                {_skipAuthRefresh: true, _skipUnauthorizedHandling: true}
            )
                .then(({data}) => this.finalizeLogin(data.token))
        },
        async refreshTokenFromUnauthorized() {
            if (!this.accessToken || this.authStatus === AUTH_STATUS.ANONYMOUS) {
                return null
            }
            await this.refreshToken({force: true, reason: 'unauthorized'})
            return this.accessToken
        },
        async externalAuth(provider) {
            this.rememberPostLoginRedirect(this.$router.currentRoute.value.query.redirect)
            const oauth = await startAccountOAuth(provider)
            return this.apiClient.post('/auth', {provider, ...oauth}, {_skipAuthRefresh: true}).then(({data}) => {
                if (data.redirect_url) {
                    window.location.href = data.redirect_url
                } else {
                    console.error('/auth response is missing redirect_url', JSON.stringify(data))
                }
            })
        },
        loginSuccessful(token) {
            return this.finalizeLogin(token)
        },
        normalizePostLoginRedirect(redirect) {
            const value = Array.isArray(redirect) ? redirect[0] : redirect
            if (typeof value !== 'string' || !value.startsWith('/') || value.startsWith('//')) {
                return null
            }

            const resolved = this.$router.resolve(value)
            if (['login', 'callback'].includes(resolved.name)) {
                return null
            }

            return value
        },
        rememberPostLoginRedirect(redirect) {
            if (!canUseWindow()) {
                return
            }

            const normalizedRedirect = this.normalizePostLoginRedirect(redirect)
            if (!normalizedRedirect) {
                return
            }

            sessionStorage.setItem(POST_LOGIN_REDIRECT_STORAGE_KEY, normalizedRedirect)
        },
        consumePostLoginRedirect() {
            const routeRedirect = this.normalizePostLoginRedirect(this.$router.currentRoute.value.query.redirect)
            let storedRedirect = null

            if (canUseWindow()) {
                storedRedirect = this.normalizePostLoginRedirect(sessionStorage.getItem(POST_LOGIN_REDIRECT_STORAGE_KEY))
                sessionStorage.removeItem(POST_LOGIN_REDIRECT_STORAGE_KEY)
            }

            return routeRedirect || storedRedirect
        },
        async logout(onlyTokenRemove = false) {
            if (this.loggingOut) {
                return
            }

            this.loggingOut = true

            try {
                if (!onlyTokenRemove && this.accessToken) {
                    try {
                        if (this.refreshPromise) {
                            await this.refreshPromise
                        }
                        await this.ensureFreshSession('logout')
                    } catch (_) {
                        // A failed refresh has already cleared an expired session.
                    }
                }

                await this.clearSession({
                    redirectToLogin: true,
                    remoteLogout: !onlyTokenRemove,
                    broadcast: true,
                    status: AUTH_STATUS.ANONYMOUS,
                })
            } finally {
                this.loggingOut = false
            }
        },
        handleUnauthorized({reason = 'session_expired'} = {}) {
            return this.clearSession({
                redirectToLogin: true,
                remoteLogout: false,
                status: AUTH_STATUS.ANONYMOUS,
                reason,
            })
        },
        register(userData, matchaToken = '') {
            return this.apiClient.post('/users', {user: userData, ...matchaParams(matchaToken)}, {
                _skipErrorAlert: true,
                _skipUnauthorizedHandling: true,
            }).then(({data}) => {
                if (!data?.error) {
                    this.created = true
                }
                return data
            })
        },
        apiAuthResetPassword(values, matchaToken = '') {
            return this.apiClient.post('/auth/forgot_password', {...values, ...matchaParams(matchaToken)}, {_skipAuthRefresh: true})
        },
        apiAuthChangePassword(password, password_confirmation, token) {
            return this.apiClient.post('/auth/change_password', {password, password_confirmation, token}, {_skipAuthRefresh: true})
        },
        apiChangeOwnPassword(current_password, password, password_confirmation) {
            return this.apiClient.patch('/auth/password', {
                current_password,
                password,
                password_confirmation,
            })
        },
        apiAuthAccountConfirmation(token) {
            return this.apiClient.get(`/account_confirmation/${encodeURIComponent(token)}`, {
                _skipAuthRefresh: true,
                _skipUnauthorizedHandling: true,
            })
        },
        apiUserData() {
            return this.apiClient.get('/users/me').then(({data}) => {
                const impersonation = this.validateImpersonationContext(
                    data.impersonation || null,
                    this.user
                )
                this.user.updateUserData(data.user)
                this.setImpersonationContext(
                    impersonation,
                    {broadcast: false}
                )
                if (impersonation?.target?.client) {
                    // The server-confirmed target must win over the target user's
                    // first client so UI state, API requests, and audit context agree.
                    this.user.setOrganisation(impersonation.target.client)
                }
                return data
            }).catch((error) => {
                console.error('apiUserData failed', error)
                return Promise.reject(error)
            })
        },
        apiUpdateUserData(data) {
            return this.apiClient.patch(`/users/${this.user.id}`, {user: data})
        },
        apiRequestAccountDeletion() {
            return this.apiClient.post('/deletion_request')
        },
        apiPersonalDataExport() {
            return this.apiClient.get('/users/personal_data', {responseType: 'blob'})
        },
        apiUpdateCurrentClientData(data) {
            const clientId = this.user.organisation?.id
            if (!clientId) {
                return Promise.resolve()
            }
            return this.apiClient.patch(`/clients/${clientId}`, {cp_client: data})
        },
        apiGetClients(kdnr) {
            return this.apiClient.get('/clients', {params: {kdnr}})
        },
        async applyKdnrFromRoute(routeKdnr) {
            if (typeof routeKdnr === 'undefined' || routeKdnr === null || routeKdnr === '') {
                return false
            }

            const kdnr = String(Array.isArray(routeKdnr) ? routeKdnr[0] : routeKdnr).trim()
            if (!kdnr) {
                return false
            }

            if (this.routeImpersonationPromise) {
                if (this.routeImpersonationKdnr === kdnr) {
                    return this.routeImpersonationPromise
                }

                try {
                    await this.routeImpersonationPromise
                } catch (_) {
                    // The latest navigation still gets its own impersonation attempt.
                }
                return this.applyKdnrFromRoute(kdnr)
            }

            if (this.isImpersonating) {
                const impersonatedKdnr = this.impersonation?.target?.client?.kdnr
                    || this.user.organisation?.kdnr
                if (String(impersonatedKdnr) === kdnr) {
                    return true
                }

                await this.restoreOriginalSession()
            }

            if (this.user.isStaffMember()) {
                this.routeImpersonationKdnr = kdnr
                this.routeImpersonationPromise = (async () => {
                    const {data} = await this.apiGetClients(kdnr)
                    const client = Array.isArray(data)
                        ? data.find((candidate) => String(candidate.kdnr) === kdnr)
                        : null

                    if (!client?.id) {
                        throw new Error(`Cannot impersonate customer ${kdnr}: matching client was not found`)
                    }

                    await this.apiImpersonateClient(client.id)
                    return true
                })()

                try {
                    return await this.routeImpersonationPromise
                } finally {
                    this.routeImpersonationPromise = null
                    this.routeImpersonationKdnr = null
                }
            }

            const matchingOrganisation = this.user
                .getOrganisations()
                .find((organisation) => String(organisation.kdnr) === kdnr)

            if (matchingOrganisation) {
                this.user.setOrganisation(matchingOrganisation)
                return true
            }

            const {data} = await this.apiGetClients(kdnr)
            if (!Array.isArray(data)) {
                return false
            }

            const client = data.find((candidate) => String(candidate.kdnr) === kdnr)
            if (!client) {
                return false
            }

            this.user.setOrganisation(client)
            this.user.setCurrentOrganisations(data)
            return true
        },
        async apiImpersonateClient(clientId) {
            const {data} = await this.apiClient.post(`/clients/${encodeURIComponent(clientId)}/impersonate`)
            if (!data?.token || !data?.impersonation?.id) {
                throw new Error(data?.message || 'Impersonation response is incomplete')
            }

            this.setSession(data.token, {
                preserveProfile: false,
                impersonation: data.impersonation,
            })
            await this.apiUserData()
            return data
        },
        async restoreOriginalSession() {
            if (!this.isImpersonating) {
                return false
            }

            const {data} = await this.apiClient.post('/auth/stop_impersonating')
            if (!data?.token) {
                throw new Error(data?.message || 'Stop impersonation response is missing token')
            }

            this.setSession(data.token, {
                preserveProfile: false,
                impersonation: null,
            })
            await this.apiUserData()
            return true
        },
        async stopImpersonating() {
            if (!this.isImpersonating || this.stoppingImpersonation) {
                return
            }

            this.stoppingImpersonation = true

            try {
                await this.restoreOriginalSession()

                const currentRoute = this.$router.currentRoute.value
                const hasKdnr = typeof currentRoute.query.kdnr !== 'undefined'
                if (currentRoute.name !== 'dashboard' || hasKdnr) {
                    await this.$router.replace({name: 'dashboard'})
                }
            } catch (error) {
                console.error('stopImpersonating failed', error)
                await this.clearSession({
                    redirectToLogin: true,
                    remoteLogout: false,
                    status: AUTH_STATUS.ANONYMOUS,
                    reason: 'session_expired',
                })
                throw error
            } finally {
                this.stoppingImpersonation = false
            }
        },
        fetchMyClients(force = false) {
            const orgs = this.user.getCurrentOrganisations(null)
            if (force) {
                this.allClients = null
            }
            if (force || orgs === null) {
                const params = {kdnr: this.user.organisation.kdnr}
                this.apiClient.get('/clients', {params}).then(({data}) => {
                    this.user.setCurrentOrganisations(data)
                }).catch((error) => {
                    if (error?.response?.status !== 401) {
                        console.error('fetchMyClients failed', error)
                    }
                })
            }
        },
        fetchMyLocations(force = false) {
            if (this.myLocationsLoading) {
                return Promise.resolve(this.myLocations)
            }
            if (!force && this.myLocationsFetched) {
                return Promise.resolve(this.myLocations)
            }

            const kdnr = this.user.organisation?.kdnr || this.$router.currentRoute.value.query.kdnr
            if (!kdnr) {
                this.myLocations = []
                this.myLocationsTotal = 0
                return Promise.resolve(this.myLocations)
            }

            this.myLocationsLoading = true
            return this.apiClientV2.get('/locations', {params: {kdnr, size: 200}}).then(({data}) => {
                if (data?.error) {
                    console.error('fetchMyLocations returned error', data)
                    return this.myLocations
                }
                this.myLocations = Array.isArray(data?.locations) ? data.locations : []
                this.myLocationsTotal = Number(data?.totalRecords) || this.myLocations.length
                this.myLocationsFetched = true
                return this.myLocations
            }).catch((error) => {
                if (error?.response?.status !== 401) {
                    console.error('fetchMyLocations failed', error)
                }
                return Promise.reject(error)
            }).finally(() => {
                this.myLocationsLoading = false
            })
        },
        searchMyLocations(query) {
            const kdnr = this.user.organisation?.kdnr || this.$router.currentRoute.value.query.kdnr
            if (!kdnr) {
                return Promise.resolve([])
            }

            const params = {kdnr, q: String(query || '').trim(), size: 50}
            return this.apiClientV2.get('/locations', {params}).then(({data}) => {
                return Array.isArray(data?.locations) ? data.locations : []
            })
        },
        searchClients(addressClient, {force = false} = {}) {
            if (force || addressClient.search?.length > 3) {
                addressClient.searching = true
                const params = {
                    q: addressClient.search
                }
                return this.apiClient.get('/clients/search', {params}).then(({data}) => {
                    addressClient.searchResult = data
                    addressClient.searching = false
                    return data
                }).catch((error) => {
                    addressClient.searching = false
                    if (error?.response?.status !== 401) {
                        console.error('searchClients failed', error)
                    }
                    return Promise.reject(error)
                })
            } else {
                addressClient.searchResult = []
                return Promise.resolve([])
            }
        },
        searchLocations(addressClient, {force = false} = {}) {
            if (force || addressClient.search?.length > 3) {
                addressClient.searching = true
                const params = {q: addressClient.search}
                return this.apiClient.get('/locations/search', {params}).then(({data}) => {
                    addressClient.searchResult = data
                    addressClient.searching = false
                    return data
                }).catch((error) => {
                    addressClient.searching = false
                    if (error?.response?.status !== 401) {
                        console.error('searchLocations failed', error)
                    }
                    return Promise.reject(error)
                })
            } else {
                addressClient.searchResult = []
                return Promise.resolve([])
            }
        },
        createClientFromAddress(address) {
            const clientAttributes = {
                name: address.name,
                street: address.street,
                zip: address.postcode,
                town: address.city,
                country: address.country,
                latitude: address.lat,
                longitude: address.lon,
                parent_kdnr: this.user.organisation.kdnr,
                account_type: 'Location',
            }
            return this.apiClient.post('/clients', {cp_client: clientAttributes}).then(({data}) => {
                if (data.error) {
                    Swal.fire({
                        title: 'Error!',
                        text: JSON.stringify(data.message),
                        icon: 'error',
                        confirmButtonText: 'Ok'
                    })
                } else {
                    const createdClient = Array.isArray(data) ? data[0] : data
                    address.createdId = createdClient?.id
                    this.fetchMyClients(true)
                    return createdClient
                }
            })
        },
        createLocationFromAddress(address) {
            const kdnr = this.user.organisation?.kdnr || this.$router.currentRoute.value.query.kdnr
            const platform_location = {
                name: address.name,
                street: address.street,
                zip: address.postcode || address.zip,
                town: address.city || address.town,
                country_code: address.country_code || address.country,
                latitude: address.lat,
                longitude: address.lon,
            }
            return this.apiClient.post('/locations', {kdnr, platform_location}).then(({data}) => {
                if (data.error) {
                    Swal.fire({
                        title: 'Error!',
                        text: JSON.stringify(data.message),
                        icon: 'error',
                        confirmButtonText: 'Ok'
                    })
                    return null
                }
                return data
            })
        },
    },
})
